import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import ReactSelect from 'react-select';
import { FaPlus, FaTimes, FaPenAlt, FaExclamationTriangle, FaCheckCircle, FaMinus, FaSearch } from 'react-icons/fa';

const ExpenseCategories = () => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [categories, setCategories] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [analysisCodes, setAnalysisCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '42px',
            borderRadius: '0.375rem',
            borderColor: state.isFocused ? '#FF5733' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(255,87,51,0.12)' : 'none',
            '&:hover': { borderColor: '#FF5733' }
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 30
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 50
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#fff7ed' : state.isSelected ? '#ffedd5' : '#fff',
            color: '#1f2937'
        })
    };

    const glAccountOptions = glAccounts.map((account) => ({
        value: account.account_code,
        label: `${account.account_code} - ${account.account_name}`
    }));

    const analysisCodeOptions = analysisCodes.map((code) => ({
        value: code.code,
        label: `${code.code} - ${code.description}`
    }));

    const [form, setForm] = useState({
        code: '',
        name: '',
        description: '',
        is_vehicle_related: false,
        is_office_related: false,
        requires_approval: false,
        approval_threshold: 5000,
        requires_vendor: false,
        // category-level GL mapping (only used when subcat_count === 0)
        cat_debit_account_code: '',
        cat_analysis_code: '',
        cat_gl_notes: '',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchCategories(), fetchGlAccounts(), fetchAnalysisCodes()]);
        setLoading(false);
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/expenses/categories`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            if (res.data?.success) {
                const data = (res.data.data || []).slice();
                data.sort((a, b) => (a.code > b.code ? 1 : a.code < b.code ? -1 : 0));
                setCategories(data);
            }
        } catch (e) { console.error('Error fetching categories', e); }
    };

    const fetchGlAccounts = async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/expenses/gl-accounts`, { headers: { Authorization: `Bearer ${authToken}` } });
            if (res.data.success) setGlAccounts(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    const fetchAnalysisCodes = async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/owner-analysis-codes`, { headers: { Authorization: `Bearer ${authToken}` } });
            if (res.data.success) setAnalysisCodes(res.data.data || []);
        } catch (e) { console.error(e); }
    };

    const openCreate = () => {
        setEditing(null);
        setError(null);
        setForm({ code: '', name: '', description: '', is_vehicle_related: false, is_office_related: false, requires_approval: false, approval_threshold: 5000, requires_vendor: false, cat_debit_account_code: '', cat_analysis_code: '', cat_gl_notes: '' });
        setShowDialog(true);
    };

    const openEdit = (cat) => {
        setEditing(cat);
        setError(null);
        setForm({
            code: cat.code,
            name: cat.name,
            description: cat.description || '',
            is_vehicle_related: !!cat.is_vehicle_related,
            is_office_related: !!cat.is_office_related,
            requires_approval: !!cat.requires_approval,
            approval_threshold: cat.approval_threshold || 5000,
            requires_vendor: !!cat.requires_vendor,
            cat_debit_account_code: cat.cat_debit_account_code || '',
            cat_analysis_code: cat.cat_analysis_code || '',
            cat_gl_notes: cat.cat_gl_notes || '',
        });
        setShowDialog(true);
    };

    const handleSubmit = async () => {
        if (!form.is_vehicle_related && !form.is_office_related) {
            setError('Please select a type — Vehicle or Office.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            let savedId;
            if (editing) {
                const res = await axios.put(`${rootUrl}/api/expenses/categories/${editing.id}`, {
                    name: form.name,
                    description: form.description,
                    is_vehicle_related: form.is_vehicle_related,
                    is_office_related: form.is_office_related,
                    requires_approval: form.requires_approval,
                    approval_threshold: form.approval_threshold,
                    requires_vendor: form.requires_vendor,
                }, { headers: { Authorization: `Bearer ${authToken}` } });
                if (!res.data.success) throw new Error(res.data.message);
                savedId = editing.id;
            } else {
                const res = await axios.post(`${rootUrl}/api/expenses/categories`, {
                    code: form.code,
                    name: form.name,
                    description: form.description,
                    is_vehicle_related: form.is_vehicle_related,
                    is_office_related: form.is_office_related,
                    requires_approval: form.requires_approval,
                    approval_threshold: form.approval_threshold,
                    requires_vendor: form.requires_vendor,
                }, { headers: { Authorization: `Bearer ${authToken}` } });
                if (!res.data.success) throw new Error(res.data.message);
                savedId = res.data.data.id;
            }

            // Save category-level GL mapping if a debit account is provided
            // Only meaningful when there are no subcategories, but we save it regardless
            if (form.cat_debit_account_code) {
                await axios.put(
                    `${rootUrl}/api/expenses/categories/${savedId}/gl-mapping`,
                    { debit_account_code: form.cat_debit_account_code, analysis_code: form.cat_analysis_code || null, notes: form.cat_gl_notes || null },
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
            }

            await fetchCategories();
            setShowDialog(false);
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (cat) => {
        try {
            await axios.put(`${rootUrl}/api/expenses/categories/${cat.id}`, { is_active: !cat.is_active }, { headers: { Authorization: `Bearer ${authToken}` } });
            fetchCategories();
        } catch (e) { console.error('Error toggling active', e); }
    };

    const glMappingCell = (c) => {
        const total = parseInt(c.subcat_count || 0, 10);
        const mapped = parseInt(c.mapped_subcat_count || 0, 10);

        if (total === 0) {
            // No subcategories - GL mapping must be at category level
            if (c.cat_debit_account_code) {
                return (
                    <div className="flex items-center gap-1 min-w-0">
                        <FaCheckCircle className="text-green-500 text-xs flex-shrink-0" />
                        <span className="text-xs text-green-700 font-medium font-mono flex-shrink-0">{c.cat_debit_account_code}</span>
                        <span className="text-xs text-gray-500 truncate" title={c.cat_debit_account_name}>- {c.cat_debit_account_name}</span>
                        {c.cat_analysis_code && (
                            <span className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-1.5 py-0.5 rounded-full font-mono flex-shrink-0">{c.cat_analysis_code}</span>
                        )}
                    </div>
                );
            }
            return (
                <div className="flex items-center gap-1.5">
                    <FaExclamationTriangle className="text-amber-500 text-xs flex-shrink-0" />
                    <span className="text-xs text-amber-700 font-medium">No GL mapping - expenses cannot post</span>
                </div>
            );
        }

        // Has subcategories - show subcategory mapping health
        const unmapped = total - mapped;
        if (unmapped === 0) {
            return (
                <div className="flex items-center gap-1.5">
                    <FaCheckCircle className="text-green-500 text-xs" />
                    <span className="text-xs text-green-700 font-medium">All {mapped} subcategories mapped</span>
                </div>
            );
        }
        return (
            <div className="flex items-center gap-1.5">
                <FaExclamationTriangle className="text-amber-500 text-xs" />
                <span className="text-xs text-amber-700 font-medium">
                    {mapped}/{total} subcategories mapped
                    <span className="text-amber-500 ml-1">({unmapped} missing)</span>
                </span>
            </div>
        );
    };

    // Count categories needing attention
    const needsAttention = categories.filter(c => {
        const total = parseInt(c.subcat_count || 0, 10);
        const mapped = parseInt(c.mapped_subcat_count || 0, 10);
        if (total === 0) return !c.cat_debit_account_code;
        return mapped < total;
    }).length;

    const filteredCategories = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return categories;
        return categories.filter(
            (c) => c.name?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q)
        );
    }, [categories, searchTerm]);

    const editingHasNoSubcats = editing && parseInt(editing.subcat_count || 0, 10) === 0;

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Expense Categories</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">Manage expense categories and their GL mappings</p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">

                {needsAttention > 0 && (
                    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                        <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-amber-800 font-semibold text-sm">
                                {needsAttention} categor{needsAttention === 1 ? 'y has' : 'ies have'} incomplete GL mappings
                            </p>
                            <p className="text-amber-700 text-xs mt-0.5">
                                Expenses in these categories will fail GL posting validation until mappings are set.
                            </p>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="flex flex-col gap-4 mb-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-[#2a2e30]">Category List</h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {searchTerm.trim() ? `${filteredCategories.length} of ${categories.length}` : `${categories.length} total`}
                                </p>
                            </div>
                            <button
                                onClick={openCreate}
                                className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-4 py-2"
                            >
                                <FaPlus className="text-xs" /> Add Category
                            </button>
                        </div>
                        <div className="relative max-w-sm">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name or code..."
                                className="w-full border border-gray-300 rounded-sm pl-8 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    aria-label="Clear search"
                                >
                                    <FaTimes className="text-xs" />
                                </button>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <p className="text-center py-10 text-gray-500 text-sm">Loading...</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[800px] table-fixed text-left text-xs">
                                <colgroup>
                                    <col className="w-14" />
                                    <col className="w-40" />
                                    <col className="w-20" />
                                    <col className="w-14" />
                                    <col />
                                    <col className="w-20" />
                                    <col className="w-20" />
                                </colgroup>
                                <thead>
                                    <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                        <th className="p-2 text-left text-xs font-semibold leading-none text-blue-gray-700">Code</th>
                                        <th className="p-2 text-left text-xs font-semibold leading-none text-blue-gray-700">Name</th>
                                        <th className="p-2 text-left text-xs font-semibold leading-none text-blue-gray-700">Type</th>
                                        <th className="p-2 text-center text-xs font-semibold leading-none text-blue-gray-700">Subcats</th>
                                        <th className="p-2 text-left text-xs font-semibold leading-none text-blue-gray-700">GL Mapping</th>
                                        <th className="p-2 text-center text-xs font-semibold leading-none text-blue-gray-700">Status</th>
                                        <th className="p-2 text-center text-xs font-semibold leading-none text-blue-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCategories.map((c) => (
                                        <tr key={c.id} className="align-middle border-b border-gray-100 hover:bg-[#fff8f5]">
                                            <td className="p-2 font-mono text-xs uppercase text-slate-800">{c.code}</td>
                                            <td className="p-2 text-slate-600 truncate">
                                                <span title={c.name}>{c.name}</span>
                                            </td>
                                            <td className="p-2">
                                                {c.is_vehicle_related && !c.is_office_related && (
                                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Vehicle</span>
                                                )}
                                                {c.is_office_related && !c.is_vehicle_related && (
                                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Office</span>
                                                )}
                                                {c.is_vehicle_related && c.is_office_related && (
                                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Both</span>
                                                )}
                                                {!c.is_vehicle_related && !c.is_office_related && (
                                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">—</span>
                                                )}
                                            </td>
                                            <td className="p-2 text-center text-slate-600">
                                                {parseInt(c.subcat_count || 0, 10)}
                                            </td>
                                            <td className="p-2">{glMappingCell(c)}</td>
                                            <td className="p-2 text-center">
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                    {c.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="p-2 text-center">
                                                <div className="flex justify-center gap-1.5">
                                                    <button title="Edit" onClick={() => openEdit(c)} className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-blue-600 hover:bg-blue-50">
                                                        <FaPenAlt className="h-3 w-3" />
                                                    </button>
                                                    <button title={c.is_active ? 'Deactivate' : 'Activate'} onClick={() => toggleActive(c)} className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 text-sm font-bold">
                                                        {c.is_active ? <FaMinus className="h-3 w-3" /> : '+'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredCategories.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="py-10 text-center text-sm text-gray-400">
                                                {searchTerm.trim() ? `No categories match "${searchTerm}".` : 'No categories found.'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit / Create Dialog */}
            {showDialog && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-[#2a2e30]">
                                {editing ? `Edit Category - ${editing.code}` : 'New Category'}
                            </h3>
                            <button onClick={() => setShowDialog(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                        </div>

                        <div className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">

                            {/* Basic fields */}
                            {!editing && (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Code <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={form.code}
                                        onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                        placeholder="e.g. FUL"
                                        maxLength={10}
                                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                    />
                                    <p className="text-xs text-gray-400 mt-0.5">3 uppercase letters</p>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                                <input
                                    type="text"
                                    value={form.description}
                                    onChange={e => setForm({ ...form, description: e.target.value })}
                                    className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-2">Type <span className="text-red-500">*</span></label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="expense_type"
                                            checked={form.is_vehicle_related && !form.is_office_related}
                                            onChange={() => setForm({ ...form, is_vehicle_related: true, is_office_related: false })}
                                        />
                                        Vehicle
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="expense_type"
                                            checked={form.is_office_related && !form.is_vehicle_related}
                                            onChange={() => setForm({ ...form, is_vehicle_related: false, is_office_related: true })}
                                        />
                                        Office
                                    </label>
                                </div>
                                {!form.is_vehicle_related && !form.is_office_related && (
                                    <p className="text-xs text-red-500 mt-1">Select a type — Vehicle or Office.</p>
                                )}
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="requires_vendor"
                                    checked={form.requires_vendor}
                                    onChange={e => setForm({ ...form, requires_vendor: e.target.checked })}
                                    className="w-4 h-4 accent-[#FF5733]"
                                />
                                <label htmlFor="requires_vendor" className="text-sm text-gray-700">
                                    Require vendor selection for expenses in this category
                                </label>
                            </div>

                            <div className="border-t pt-4 space-y-3">
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="requires_approval"
                                        checked={form.requires_approval}
                                        onChange={e => setForm({ ...form, requires_approval: e.target.checked })}
                                        className="w-4 h-4 accent-[#FF5733]"
                                    />
                                    <label htmlFor="requires_approval" className="text-sm text-gray-700">
                                        Approval Required
                                    </label>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                                        Approval Threshold
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={form.approval_threshold}
                                        onChange={e => setForm({
                                            ...form,
                                            approval_threshold: e.target.value === '' ? '' : Number(e.target.value)
                                        })}
                                        disabled={!form.requires_approval}
                                        placeholder="5000"
                                        className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733] disabled:bg-gray-100 disabled:text-gray-400"
                                    />
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        Expenses at or above this amount go to pending approval. Lower amounts are auto-approved.
                                    </p>
                                </div>
                            </div>

                            {/* Category-level GL Mapping */}
                            {/* Always show on create; show on edit only when category has no subcategories */}
                            {(!editing || editingHasNoSubcats) && (
                                <div className="border-t pt-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <h4 className="text-sm font-semibold text-[#2a2e30]">GL Mapping</h4>
                                        {editingHasNoSubcats && (
                                            <span className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full">
                                                Category has no subcategories - mapping set here
                                            </span>
                                        )}
                                        {!editing && (
                                            <span className="text-xs text-gray-400">(optional - required if no subcategories will be added)</span>
                                        )}
                                    </div>

                                    {form.cat_debit_account_code ? (
                                        <div className="flex items-center gap-1.5 mb-3">
                                            <FaCheckCircle className="text-green-500 text-xs" />
                                            <span className="text-xs text-green-700 font-medium">GL mapping set</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1.5 mb-3">
                                            <FaExclamationTriangle className="text-amber-400 text-xs" />
                                            <span className="text-xs text-amber-700">Not mapped - expenses cannot be posted to GL</span>
                                        </div>
                                    )}

                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">GL Debit Account</label>
                                        <ReactSelect
                                            options={glAccountOptions}
                                            value={glAccountOptions.find((option) => option.value === form.cat_debit_account_code) || null}
                                            onChange={(option) => setForm({ ...form, cat_debit_account_code: option?.value || '' })}
                                            isSearchable
                                            isClearable
                                            styles={selectStyles}
                                            placeholder="Select GL account"
                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                            menuPosition="fixed"
                                        />
                                            <p className="text-xs text-gray-400 mt-0.5">The expense account debited when posting to GL</p>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">Analysis Code</label>
                                        <ReactSelect
                                            options={analysisCodeOptions}
                                            value={analysisCodeOptions.find((option) => option.value === form.cat_analysis_code) || null}
                                            onChange={(option) => setForm({ ...form, cat_analysis_code: option?.value || '' })}
                                            isSearchable
                                            isClearable
                                            styles={selectStyles}
                                            placeholder="None"
                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                            menuPosition="fixed"
                                        />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                                            <input
                                                type="text"
                                                value={form.cat_gl_notes}
                                                onChange={e => setForm({ ...form, cat_gl_notes: e.target.value })}
                                                placeholder="Optional notes about this GL mapping"
                                                className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {editing && !editingHasNoSubcats && (
                                <div className="border-t pt-4">
                                    <p className="text-xs text-gray-400 flex items-center gap-1.5">
                                        <FaCheckCircle className="text-blue-400" />
                                        This category has {parseInt(editing.subcat_count || 0, 10)} subcategor{parseInt(editing.subcat_count || 0, 10) === 1 ? 'y' : 'ies'} - GL mapping is managed per subcategory.
                                    </p>
                                </div>
                            )}

                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p className="text-red-700 text-xs">{error}</p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 px-6 py-4 border-t">
                            <button onClick={() => setShowDialog(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-sm px-4 py-2">
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64520] disabled:opacity-50 text-white text-sm font-semibold rounded-sm px-4 py-2"
                            >
                                <FaPlus className="text-xs" />
                                {saving ? 'Saving...' : editing ? 'Update' : 'Add Category'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpenseCategories;



